import { useCallback, useMemo } from 'react';
import { FUNCTIONAL_BUCKETS } from './V2';
import { BUCKET_NAMES, SPECIAL_OTHER_BUCKETS_WITH_GRID_VIEW } from 'src/constants/globalConstants';
import { getFeaturedBuckets, getPersonalityTraits } from 'src/utils/helperFunctions';
import { getTraitBand } from '../oprations';
import { roundOff } from 'src/pages/PdfReport/operations';

// scoreInk: the bucket's score in its band's ink, not its fill (the web report's 4.0 cards).
const useCreatedBucketsFromReport = ({ reportData, reportPdfView, setActiveBucketId, summaryOnly = false, scoreInk = false }) => {
  // Compact bucket layout: trait scores shown inline and no drill-down button.
  // Always on in the PDF view; also on for mini-report companies on the web report.
  const isSummaryLayout = reportPdfView || summaryOnly;
  /** Special buckets */
  const functionalBuckets = useMemo(() => {
    const buckets = getFeaturedBuckets(reportData?.buckets, FUNCTIONAL_BUCKETS);
    return {
      name: "FUNCTIONAL SKILLS",
      description: "How proficient they are on their domain centric knowledge",
      buckets,
    };
  }, [reportData?.buckets]);

  const communicationBuckets = useMemo(() => {
    const buckets = getFeaturedBuckets(reportData?.buckets, [
      BUCKET_NAMES.COMMUNICATION,
    ]);
    return {
      name: "COMMUNICATION SKILLS",
      description: "How they understand speech and express themselves",
      buckets,
    };
  }, [reportData?.buckets]);

  const competenciesBuckets = useMemo(() => {
    const buckets = getFeaturedBuckets(reportData?.buckets, [
      BUCKET_NAMES.COMPETENCIES,
    ]);
    return {
      name: "COMPETENCIES",
      description: "How they control & conduct themselves, solve problems and work with others and react to different situations",
      buckets,
    };
  }, [reportData?.buckets]);

  const aptitudeBuckets = useMemo(() => {
    const buckets = getFeaturedBuckets(reportData?.buckets, [
      BUCKET_NAMES.APTITUDE_AND_REASONING,
    ]);
    return {
      name: "APTITUDES & REASONING",
      description: "How they interpret information, apply logic to solve problems and draw conclusions",
      buckets,
    };
  }, [reportData?.buckets]);

  const personalityBuckets = useMemo(() => {
    const traits = getPersonalityTraits(reportData?.buckets, [
      BUCKET_NAMES.PERSONALITY_DIMENSIONS,
    ]);
    const personalityBuckets = reportData?.buckets?.filter(({ name }) => {
      return [BUCKET_NAMES.PERSONALITY_DIMENSIONS].includes(name);
    });
    const bucketAnalysis = personalityBuckets?.length > 0 ? personalityBuckets[0]?.bucketAnalysis : null;
    const enableBucketAnalysis = personalityBuckets?.length > 0 ? personalityBuckets[0]?.enableBucketAnalysis : null;
    return {
      name: BUCKET_NAMES.PERSONALITY_DIMENSIONS,
      traits,
      bucketAnalysis,
      enableBucketAnalysis,
    };
  }, [reportData?.buckets]);

  const personalityBucketsIPIPLong = useMemo(() => {
    const traits = getPersonalityTraits(reportData?.buckets, [
      BUCKET_NAMES.IPIP_LONG,
    ]);
    return {
      name: BUCKET_NAMES.IPIP_LONG,
      traits,
    };
  }, [reportData?.buckets]);

  const personalityBucketsIPIPShort = useMemo(() => {
    const traits = getPersonalityTraits(reportData?.buckets, [
      BUCKET_NAMES.IPIP_SHORT,
    ]);
    return {
      name: BUCKET_NAMES.IPIP_SHORT,
      traits,
    };
  }, [reportData?.buckets]);

  // Check if reportData?.buckets contain only Personality bucket
  // True -> Hide Fitscore Component
  const checkOnlyPersonalityBucket = useMemo(() => {
    if (reportData?.buckets
      && reportData?.buckets.length === 1
      && (
        reportData?.buckets[0].name === BUCKET_NAMES.PERSONALITY_DIMENSIONS ||
        reportData?.buckets[0].name === BUCKET_NAMES.IPIP_LONG ||
        reportData?.buckets[0].name === BUCKET_NAMES.IPIP_SHORT
      )
    ) return true;
    return false
  }, [reportData?.buckets]);

  const otherBucketsWithGridView = useMemo(() => {
    if (!reportData?.buckets || reportData.buckets.length === 0) return [];
    return reportData.buckets.filter(bucket => SPECIAL_OTHER_BUCKETS_WITH_GRID_VIEW.includes(bucket.name));
  }, [reportData?.buckets]);

  // Buckets not already handled by special sections
  const otherBuckets = useMemo(() => {
    if (!reportData?.buckets || reportData.buckets.length === 0) return [];

    // Bucket names that are already handled
    const handledBucketNames = new Set([
      ...FUNCTIONAL_BUCKETS,
      BUCKET_NAMES.COMMUNICATION,
      BUCKET_NAMES.COMPETENCIES,
      BUCKET_NAMES.APTITUDE_AND_REASONING,
      BUCKET_NAMES.PERSONALITY_DIMENSIONS,
      BUCKET_NAMES.IPIP_LONG,
      BUCKET_NAMES.IPIP_SHORT,
    ]);

    const otherBucketsList = reportData.buckets.filter(
      (bucket) => !handledBucketNames.has(bucket.name) && !SPECIAL_OTHER_BUCKETS_WITH_GRID_VIEW.includes(bucket.name)
    );

    return otherBucketsList;
  }, [reportData?.buckets]);

  const renderSpecialBuckets = useCallback((bucket) => {
    const { score: { score, lowerBound, upperBound, median } } = bucket ?? {};
    const band = bucket
      ? getTraitBand({
        score,
        lb: lowerBound,
        ub: upperBound,
        median,
      })
      : null;
    const color = band ? (scoreInk ? band.textColor : band.color) : "#000";

    return (
      <div className="overall-score">
        <div className="bucket-info">
          <h4 className="bucket-info--score" style={{ color }}>
            {roundOff(score)}
          </h4>
          <div>
            <h4 className="bucket-info--name">{bucket.name}</h4>
            {bucket.traitsMeta && (
              <div className="traits-list">
                {Object.values(bucket?.traitsMeta ?? {}).map(({ id, score, displayName, upperBound, lowerBound, median }) => {
                  // Colour the score by its band, matching the mini PDF's trait chips.
                  const traitBand = getTraitBand({
                    score,
                    lb: lowerBound?.score,
                    ub: upperBound?.score,
                    median,
                  });

                  return (
                    <h2
                      key={id}
                      className={`trait-box small v2 v2--single-row-bucket`}
                    >
                      {isSummaryLayout && (
                        <>
                          <span style={{ color: traitBand.textColor }}>{roundOff(score)}</span>
                          <span className="trait-score-separator">-</span>
                        </>
                      )}
                      {displayName}
                    </h2>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {
          !isSummaryLayout &&
          <div className="bucket-info--view-btn-container">
            <button
              className="bucket-info--view-btn"
              onClick={() => setActiveBucketId(bucket.id)}
            >
              View Details
            </button>
          </div>
        }
      </div>
    )
  }, [isSummaryLayout, setActiveBucketId, scoreInk])

  return {
    functionalBuckets,
    communicationBuckets,
    competenciesBuckets,
    aptitudeBuckets,
    personalityBuckets,
    personalityBucketsIPIPLong,
    personalityBucketsIPIPShort,
    checkOnlyPersonalityBucket,
    renderSpecialBuckets,
    otherBuckets,
    otherBucketsWithGridView
  };
};

export default useCreatedBucketsFromReport;
